/**
 * chatRuntime.ts — the SSE stream parser for the backend's chat wire format.
 *
 * Single translation point between the backend's hand-rolled SSE part format
 * (see backend/chat/sse.py) and the frontend consumers: the chat history hook
 * (src/chat/useChatHistory.ts, the external-store send path) and the AskBox.
 * The provider/model choice and history assembly live in useChatHistory +
 * chatStore; this module is deliberately wire-only.
 *
 * Backend SSE parts:
 *   {type:'text', delta}             — a streamed text chunk
 *   {type:'tool_call', id, name, args} — a tool invocation started
 *   {type:'tool_result', id, result}   — the tool's result
 *   {type:'tool_error', id, message}   — the tool failed
 *   {type:'error', message}     — terminal; the turn failed
 *   {type:'finish'}             — terminal; the turn is complete
 */

/** One parsed SSE event from the backend. */
export interface ChatSsePart {
  type: 'text' | 'tool_call' | 'tool_result' | 'tool_error' | 'error' | 'finish';
  delta?: string;
  id?: string;
  name?: string;
  args?: unknown;
  result?: unknown;
  message?: string;
}

/**
 * Read an SSE stream, yielding one parsed part per `data:` event. Robust to
 * partial chunks across reads.
 */
export async function* readChatSse(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<ChatSsePart> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buffer.indexOf('\n\n')) >= 0) {
        const part = parseEventBlock(buffer.slice(0, idx));
        buffer = buffer.slice(idx + 2);
        if (part) yield part;
      }
    }
    if (buffer.trim()) {
      const part = parseEventBlock(buffer);
      if (part) yield part;
    }
  } finally {
    reader.releaseLock();
  }
}

function parseEventBlock(raw: string): ChatSsePart | null {
  for (const line of raw.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed.startsWith('data:')) continue;
    const payload = trimmed.slice(5).trim();
    if (!payload) continue;
    try {
      return JSON.parse(payload) as ChatSsePart;
    } catch {
      continue; // skip malformed lines
    }
  }
  return null;
}
